import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ButtonComponent, DatepickerComponent, DropdownSelectComponent, ListTableComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { AuthorizeDirective } from '../../../../shared/authorization/authorize.directive.';
import { FocusTrapDirective } from '../../../../shared/directives/focus-trap.directive';
import { LeadDetailsService } from '../../../../services/lead-details.service';
import { LookupService } from '../../../../services/lookup.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { LoggerService } from '../../../../shared/services/logger.service';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';

@Component({
  selector: 'ppl-lead-fms-data',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgZorroModule,
    ListTableComponent,
    ButtonComponent,
    DatepickerComponent,
    DropdownSelectComponent,
    AuthorizeDirective,
    FocusTrapDirective,
  ],
  templateUrl: './lead-fms-data.component.html',
  styleUrl: './lead-fms-data.component.css',
})
export class LeadFmsDataComponent implements OnInit, OnChanges {
  @Input() pageId = '';
  @Input() tabId = '';
  @Input() readOnly = false;
  @Input() programCode = '';
  @Input() selectedLeadId: any;

  @Output() leadUpdated = new EventEmitter<boolean>();

  mode: 'read' | 'add' = 'read';
  fmsForm!: FormGroup;
  gridData: any[] = [];
  fmsModelOptions: LookupValueItem[] = [];
  isSaving = false;
  errorMessage = '';
  minStartDate: Date | null = null;

  cols = [
    { field: 'fmsModel', header: 'FMS Model', jsonpath: 'fmsModel', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'effective_from_date', header: 'From', jsonpath: 'effective_from_date', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'effective_to_date', header: 'To', jsonpath: 'effective_to_date', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  ];

  constructor(
    private fb: FormBuilder,
    private leadDetailsService: LeadDetailsService,
    private lookupService: LookupService,
    private notificationService: NotificationService,
    private dateFormatter: DateTimeFormatterService,
    private logger: LoggerService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.fmsModelOptions = this.lookupService.getLookupValue('Fms Model');
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['programCode'] || changes['selectedLeadId']) && this.programCode && this.selectedLeadId) {
      this.loadFmsData();
    }
  }

  private computeMinStartDate(): void {
    if (!this.gridData.length) {
      this.minStartDate = null;
      return;
    }
    const parseLocalDate = (dateStr: string): Date => {
      const [datePart] = dateStr.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    const latest = this.gridData.reduce((max, row) => {
      const d = parseLocalDate(row.effective_from_date);
      return d > max ? d : max;
    }, parseLocalDate(this.gridData[0].effective_from_date));
    this.minStartDate = latest;
  }

  private startDateValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !this.minStartDate) return null;
      const selected = new Date(control.value);
      const min = this.minStartDate;
      const selectedDay = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate());
      return selectedDay < min ? { minStartDate: true } : null;
    };
  }

  private buildForm(): void {
    this.fmsForm = this.fb.group({
      fmsMode: [null, Validators.required],
      startDate: [null, [Validators.required, this.startDateValidator()]],
    });
  }

  loadFmsData(): void {
    const payload = {
      pageName: 'FmsModelList',
      programCode: this.programCode,
      condition: 'and',
      recordsPerPage: 20,
      startingPageNumber: '1',
      orderByColumn: ['id'],
      orderBy: ['desc'],
      searchParam: [
        {
          jsonPath: 'participant_id',
          value: this.selectedLeadId,
          operator: 'Equal',
        },
      ],
    };

    this.leadDetailsService.getFmsData(payload).subscribe({
      next: (response: any) => {
        this.gridData = response?.responsedata ?? [];
        this.computeMinStartDate();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.logger.info('Error fetching FMS data', err);
      },
    });
  }

  add(): void {
    this.mode = 'add';
    this.errorMessage = '';
    this.fmsForm.reset();
    this.cdr.markForCheck();
  }

  cancel(): void {
    this.mode = 'read';
    this.errorMessage = '';
    this.fmsForm.reset();
    this.cdr.markForCheck();
  }

  save(): void {
    if (this.fmsForm.invalid) {
      this.fmsForm.markAllAsTouched();
      return;
    }

    const { fmsMode, startDate } = this.fmsForm.value;
    const payload = {
      programCode: this.programCode,
      fmsMode,
      participantId: this.selectedLeadId,
      startDate: this.dateFormatter.formatToString(startDate),
    };

    this.isSaving = true;
    this.leadDetailsService.addFmsModel(payload).subscribe({
      next: (response: any) => {
        this.isSaving = false;
      // If the response contains a message that does not indicate success, show an error notification
      // Message is being pulled from the API response to handle any specific error messages returned by the backend
        if (response?.status === 200 && !response?.responsedata?.message?.toLowerCase().includes('success')
        ) {
          this.notificationService.alert('error', 'Error', response.responsedata.message);
          return;
        }
        this.notificationService.alert('success', 'Success', 'FMS Model added successfully');
        this.cancel();
        this.loadFmsData();
        this.leadUpdated.emit(true);
      },
      error: (err) => {
        this.isSaving = false;
        this.logger.info('Error adding FMS model', err);
        this.notificationService.alert('error', 'Error', 'Failed to add FMS Model');
        this.cdr.markForCheck();
      },
    });
  }
}
